import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChartersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    boat: '',
    duration: '',
    date: '',
    num_people: 4,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const boats = [
    {
      name: 'Screaming Eagle',
      emoji: '🦅',
      description: 'Fast, powerful, and ready for offshore adventures! Perfect for targeting big Halibut and Ling Cod.',
      images: [
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/9dj5tiws_IMG_3559%202.JPG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/ladhck1a_IMG_4269.JPG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/51wqhh0e_IMG_7820%202.JPG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/yb2yjyhs_IMG_9029.JPEG',
      ],
    },
    {
      name: 'Seahorse',
      emoji: '🐴',
      description: 'Smooth, comfortable, and built for all-day fishing trips. Great for families and groups!',
      images: [
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/hdkg8zb7_IMG_6970.JPEG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/rdnpqov5_IMG_2911.JPG',
        'https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/ic0l3y7z_IMG_2922.JPG',
      ],
    },
  ];

  const [currentImageIndex, setCurrentImageIndex] = React.useState({
    'Screaming Eagle': 0,
    'Seahorse': 0,
  });

  const nextImage = (boatName, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [boatName]: (prev[boatName] + 1) % imagesLength
    }));
  };

  const prevImage = (boatName, imagesLength) => {
    setCurrentImageIndex(prev => ({
      ...prev,
      [boatName]: (prev[boatName] - 1 + imagesLength) % imagesLength
    }));
  };

  const pricingOptions = [
    { duration: '4 hours', price: '$1,000', people: 'Up to 4 people', extra: '$200 per extra head' },
    { duration: '8 hours', price: '$1,800', people: 'Up to 4 people', extra: '$200 per extra head' },
    { duration: '10 hours', price: '$2,000', people: 'Up to 4 people', extra: '$200 per extra head' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/charter-booking`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        boat: '',
        duration: '',
        date: '',
        num_people: 4,
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] flex items-center justify-center">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://customer-assets.emergentagent.com/job_berry-isle-reborn/artifacts/6nprh4iw_IMG_4200.JPG"
            alt="Fishing"
            className="w-full h-full object-cover object-bottom"
          />
        </div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl brand-charters mb-4" data-testid="charters-hero-title">
            Fishing Charters 🎣
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Reel in the catch of a lifetime! Choose your boat, pick your adventure, and let's go fishing!
          </p>
        </div>
      </section>

      {/* What You'll Catch Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#FF69B4' }}>
            What You'll Catch 🐟
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#20B2AA]/10 to-[#5DADE2]/10 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#20B2AA' }}>
                🌊 Inshore Fishing
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-2xl mr-3">•</span>
                  <div>
                    <strong>Chinook (King) Salmon:</strong> Year-round fighters that taste amazing! 
                    These bad boys put up a serious fight.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">•</span>
                  <div>
                    <strong>Coho (Silver) Salmon:</strong> Show up late June/early July. 
                    Watch them do acrobatic stunts! Best tasting salmon, hands down.
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-[#FF69B4]/10 to-[#FFB6C1]/10 rounded-2xl p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#FF69B4' }}>
                🌊 Offshore Fishing
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-2xl mr-3">•</span>
                  <div>
                    <strong>Halibut:</strong> HUGE! You'll feel like a superhero after reeling one in. 
                    Absolutely delicious any way you cook it!
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">•</span>
                  <div>
                    <strong>Ling Cod:</strong> These get BIG! Perfect for fish 'n chips. Mmmm!
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">•</span>
                  <div>
                    <strong>Rock Fish:</strong> Smaller but super fun to catch! Packed with Omega-3s 
                    and make a yummy filet.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-2xl mr-3">•</span>
                  <div>
                    <strong>Tuna:</strong> Seasonal catches that are worth the wait! 
                    These powerful fish provide an incredible fight and amazing fresh sashimi.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Fleet */}
      <section className="py-16 bg-gradient-to-b from-white to-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#20B2AA' }}>
            Meet Our Boats! {boats[0].emoji} {boats[1].emoji}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {boats.map((boat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
                data-testid={`boat-${boat.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="h-64 relative overflow-hidden group">
                  <img
                    src={boat.images[currentImageIndex[boat.name]]}
                    alt={`${boat.name} ${currentImageIndex[boat.name] + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Carousel Controls */}
                  <button
                    onClick={() => prevImage(boat.name, boat.images.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Previous image"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => nextImage(boat.name, boat.images.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Next image"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {/* Image Indicators */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {boat.images.map((_, imgIndex) => (
                      <button
                        key={imgIndex}
                        onClick={() => setCurrentImageIndex(prev => ({...prev, [boat.name]: imgIndex}))}
                        className={`w-2 h-2 rounded-full transition-all ${
                          imgIndex === currentImageIndex[boat.name] 
                            ? 'bg-white w-6' 
                            : 'bg-white/60 hover:bg-white/80'
                        }`}
                        aria-label={`Go to image ${imgIndex + 1}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-3xl font-bold mb-3" style={{ color: '#FF69B4' }}>
                    {boat.emoji} {boat.name}
                  </h3>
                  <p className="text-gray-700 text-lg">{boat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12" style={{ color: '#FF69B4' }}>
            Charter Pricing 💰
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {pricingOptions.map((option, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#20B2AA] to-[#5DADE2] text-white rounded-2xl p-8 shadow-xl hover:scale-105 transition-transform duration-300"
                data-testid={`pricing-option-${index}`}
              >
                <div className="text-center">
                  <div className="text-5xl mb-4">⏱️</div>
                  <h3 className="text-3xl font-bold mb-2">{option.duration}</h3>
                  <div className="text-4xl font-bold mb-4">{option.price}</div>
                  <p className="text-lg mb-2">{option.people}</p>
                  <p className="text-sm opacity-90">{option.extra}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-xl text-gray-700">
              ✨ <strong>Mix & Match Your Day!</strong> ✨
            </p>
            <p className="text-lg text-gray-600 mt-2">
              Half-day fishing + half-day Hot Springs? Full day of touring with snacks? 
              We'll make it happen – just let us know! 🌟
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 bg-gradient-to-b from-[#F5F5F5] to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-8" style={{ color: '#20B2AA' }}>
            Book Your Charter! 🎣
          </h2>
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8" data-testid="charter-booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-email-input"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-phone-input"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Choose Your Boat *</label>
                <select
                  name="boat"
                  value={formData.boat}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-boat-select"
                >
                  <option value="">Select a boat</option>
                  <option value="Screaming Eagle">🦅 Screaming Eagle</option>
                  <option value="Seahorse">🐴 Seahorse</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Duration *</label>
                <select
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-duration-select"
                >
                  <option value="">Select duration</option>
                  <option value="4 hours">4 hours - $1,000</option>
                  <option value="8 hours">8 hours - $1,800</option>
                  <option value="10 hours">10 hours - $2,000</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Preferred Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-date-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 font-semibold mb-2">Number of People *</label>
                <input
                  type="number"
                  name="num_people"
                  value={formData.num_people}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  data-testid="booking-num-people-input"
                />
                <p className="text-sm text-gray-500 mt-1">Base rate covers up to 4 people. $200 per additional person.</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-gray-700 font-semibold mb-2">Special Requests or Questions</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#20B2AA] focus:outline-none"
                  placeholder="Any special requests? Want to mix in Hot Springs or island touring?"
                  data-testid="booking-message-textarea"
                ></textarea>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mt-6 p-4 bg-green-100 text-green-700 rounded-lg" data-testid="success-message">
                🎉 Awesome! Your booking request has been sent. We'll get back to you soon!
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-100 text-red-700 rounded-lg" data-testid="error-message">
                Oops! Something went wrong. Please try again or email us directly at joel@fishingtofino.net
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-8 w-full bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white py-4 rounded-full text-lg font-semibold hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-booking-btn"
            >
              {isSubmitting ? 'Sending...' : 'Send Booking Request 🚀'}
            </button>
          </form>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-16 bg-gradient-to-r from-[#20B2AA] to-[#5DADE2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Your Safety is Our Priority! 🛟</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <ul className="space-y-2">
              <li>✅ GPS, Radar & VHF Radio</li>
              <li>✅ Top-of-the-Line Life Jackets</li>
              <li>✅ Life Raft & Flares</li>
            </ul>
            <ul className="space-y-2">
              <li>✅ Heating Blankets</li>
              <li>✅ Two Reliable Four-Stroke Engines</li>
              <li>✅ Fully Licensed & Insured</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChartersPage;
